import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { Ablageort, ApiClient, IncomingInvoice, IncomingInvoiceFormData, IncomingInvoiceInput, IncomingInvoiceLine,
  IncomingInvoiceSaved, Steuersatz } from "@kundeninfo/api-client";
import { Arbeitsdialog, darfAbbrechen, useBeforeUnload, useFormularZustand, useSofortPopup } from "@kundeninfo/ui";
import { Auswahl, Chip, Eingabe, Formular, Hinweis, Optionen } from "@kundeninfo/ui/me-design";
import { AssignmentHint, KontierungFelder } from "../../components/KontierungFelder.js";
import type { KontierungWert } from "../../components/KontierungFelder.js";
import { KiKnopf, LoeschKnopf } from "../../components/meHuellen.js";
import { Vorgangsabschnitt } from "../../components/Vorgangsabschnitt.js";
import { useSectionFolds } from "../../components/vorgangsabschnitt/sectionFolds.js";
import { UploadFilesPanel } from "../../components/documentFiles/UploadFilesPanel.js";
import type { UploadFilesClient } from "../../components/documentFiles/UploadFilesPanel.js";
import { DOCUMENT_FILE_TEXT } from "../../components/documentFiles/texts.js";
import { errorProp, fieldErrorInsteadOf409 } from "../../components/fieldErrors.js";
import { PAYMENT_STATE_TEXT } from "../suppliers/paymentState.js";
import { storedRateText, useActiveRates } from "../suppliers/KonditionPanel.js";
import { AMOUNT_FIELD_MAX, PAYMENT_METHODS, TAX_CASES, amountField, amountForApi, cents, centsField, centsMoney, today }
  from "../suppliers/shared.js";
import { SUPPLIER_TEXT_WEB as S } from "../suppliers/texts.js";
import { LINES_MAX, NUMBER_MAX, SIGNED, chargeText, offeredCharges, shownCharges, taxFor } from "./shared.js";
import { INCOMING_TEXT_WEB as T } from "./texts.js";
import "./IncomingInvoices.css";

/**
 * DAS ERFASSUNGSPANEL einer Eingangsrechnung (Spec „Akte eines Lieferanten › Erfassen einer Eingangsrechnung“, Plan
 * Aufgabe 17): Modalpanel rechts (`Arbeitsdialog panel`, unter 1024 px vollflächig) in drei Abschnitten.
 *
 *  - **Kopf:** Art, Vertrag (belegt Zahlweise, Konto und Kostenstelle vor), Rechnungsnummer mit Dublettenhinweis am Feld,
 *    Rechnungsdatum, Eingang (vorbelegt heute), Leistungszeitraum, Fälligkeit, Zahlweise; bei gewähltem Vertrag die
 *    Posten als Kästchen — nur offene und die an DIESER Rechnung gebundenen, nie fremd gebundene (Nachtrag Controller zu
 *    Aufgabe 15); sichtbar nur die vorgeschlagenen, die gewählten und die bis zum späteren von Rechnungsdatum und
 *    Leistungsende fälligen, der Rest hinter „weitere (n)“ (Final-Fixrunde A-S1). Jahresabrechnung und Gutschrift binden
 *    keine Posten (Kästchen weg, Wahl geleert; der Server lehnt eine Gutschrift mit Posten ab, B-K6). Die Erfassungshilfe (`getIncomingInvoiceFormData`) läuft sofort bei Vertrag, Zeitraum und Datum,
 *    250 ms nach der letzten Eingabe allein in der Rechnungsnummer. Vorschlag und Anrechnung übernimmt die Maske nur,
 *    wenn sich Vertrag oder Leistungszeitraum ändern (eine Wahl von Hand bleibt sonst stehen); die Fälligkeit und die
 *    angerechneten Abschläge, solange niemand sie selbst gesetzt hat (A-K5) — sie kommen fertig vom Server, das Web
 *    rechnet keine Tage dazu.
 *  - **Beträge:** eine Zeile Netto, Steuerfall, Steuersatz, Steuer, Konto, Kostenstelle; „Aufteilen“ fügt Zeilen an
 *    (höchstens 100). Die Steuer folgt Netto × Satz, bis jemand sie selbst ändert; bei einem anderen Steuerfall steht sie
 *    gesperrt auf 0. Ein gespeicherter, inzwischen nicht mehr aktiver Steuersatz bleibt stehen (erst nach dem Laden der
 *    Sätze „nicht mehr aktiv“ genannt); kehrt die Zeile zu ihm zurück, gilt wieder ihre gespeicherte Steuer. Darunter die Summe, bei
 *    Jahresabrechnung und Gutschrift der Zahlbetrag (negativ = „Guthaben offen“).
 *  - **Datei:** `UploadFilesPanel` aus 059 mit der Ablage aus `storage`; vor dem ersten Speichern gesperrt (Sofort-Popup).
 *
 * Der Speichern-Knopf ist die Bestätigung (Dirk 216c). Nach dem ERSTEN Speichern bleibt das Panel offen — jetzt mit der
 * Ablage —, ein späteres Speichern schließt es (bleibt offen, solange der Server Warnungen meldet). Abbrechen ist der
 * eine Ausgang daneben (Rückfrage bei Ungespeichertem; `useBeforeUnload` für den Browser). Betragsform und Längen setzt
 * die Maske selbst durch, damit keine Schema-400 entsteht (Review Aufgabe 16); Befunde der Domäne `lines[i].…` stehen an
 * ihrer Zeile.
 */
export type IncomingPanelClient = UploadFilesClient & Pick<ApiClient,
  "getIncomingInvoiceFormData" | "createIncomingInvoice" | "updateIncomingInvoice" | "getIncomingInvoice"
  | "listActiveTaxRates" | "getBookingAccounts" | "getCostCentres" | "listDocumentFiles">;

type Kind = IncomingInvoice["kind"];
type TaxCase = IncomingInvoiceLine["taxCase"];
type PaymentMethod = IncomingInvoice["paymentMethod"];
type FormQuery = Parameters<ApiClient["getIncomingInvoiceFormData"]>[1];

const KINDS: readonly Kind[] = ["INVOICE", "SETTLEMENT", "CREDIT_NOTE"];
/** Nur eine Rechnung bindet Posten: Jahresabrechnung und Gutschrift nie (der Server lehnt sie mit 422 ab, B-K6). */
const bindsCharges = (k: Kind): boolean => k === "INVOICE";
const DUPLICATE_DELAY_MS = 250;

// ------------------------------------------------------------------------------------------- Entwurf

type LineDraft = KontierungWert & {
  key: number; description: string | null; net: string; taxCase: TaxCase; taxRateId: string; tax: string; taxManual: boolean;
  /** Netto, Satz und Steuer, wie gespeichert — kehrt die Zeile zu einem nicht mehr aktiven Satz zurück, gilt diese Steuer. */
  stored: { net: string; taxRateId: string; tax: string } | null;
};
type Draft = {
  number: string; kind: Kind; invoiceDate: string; receivedOn: string; periodStart: string; periodEnd: string;
  dueDate: string; dueManual: boolean; contractId: string; paymentMethod: PaymentMethod; credited: string;
  creditedManual: boolean; lines: LineDraft[]; chargeIds: string[];
};
/** Die Schlüssel der Betragszeilen kommen aus dem Zähler DIESES Panels. */
type NextKey = () => number;

const newLine = (key: NextKey, assignment: KontierungWert): LineDraft => ({
  key: key(), description: null, net: "", taxCase: "DOMESTIC", taxRateId: "", tax: "", taxManual: false, stored: null,
  ...assignment });

function draftOf(inv: IncomingInvoice | null, key: NextKey): Draft {
  if (inv === null) {
    return { number: "", kind: "INVOICE", invoiceDate: "", receivedOn: today(), periodStart: "", periodEnd: "", dueDate: "",
      dueManual: false, contractId: "", paymentMethod: "TRANSFER", credited: "", creditedManual: false, chargeIds: [],
      lines: [newLine(key, { bookingAccountId: null, costCentreId: null })] };
  }
  return {
    number: inv.supplierInvoiceNumber, kind: inv.kind, invoiceDate: inv.invoiceDate, receivedOn: inv.receivedOn,
    periodStart: inv.servicePeriodStart ?? "", periodEnd: inv.servicePeriodEnd ?? "", dueDate: inv.dueDate, dueManual: true,
    contractId: inv.supplierContractId ?? "", paymentMethod: inv.paymentMethod,
    credited: inv.creditedAmount === null ? "" : amountField(inv.creditedAmount), creditedManual: inv.creditedAmount !== null,
    chargeIds: [...inv.chargeIds],
    lines: inv.lines.map((l) => ({ key: key(), description: l.description, net: amountField(l.netAmount),
      taxCase: l.taxCase, taxRateId: l.taxRateId ?? "", tax: amountField(l.taxAmount), taxManual: true,
      stored: { net: amountField(l.netAmount), taxRateId: l.taxRateId ?? "", tax: amountField(l.taxAmount) },
      bookingAccountId: l.bookingAccountId, costCentreId: l.costCentreId })),
  };
}

/** Die Abfrage der Erfassungshilfe: nur gesetzte Angaben. */
function formQuery(companyId: string, d: Draft): FormQuery {
  const q: FormQuery = { companyId };
  if (d.contractId !== "") q.contractId = d.contractId;
  if (d.periodStart !== "") q.servicePeriodStart = d.periodStart;
  if (d.periodEnd !== "") q.servicePeriodEnd = d.periodEnd;
  if (d.number.trim() !== "") q.supplierInvoiceNumber = d.number.trim();
  if (d.invoiceDate !== "") q.invoiceDate = d.invoiceDate;
  return q;
}
/** Vorschlag und Anrechnung hängen an Vertrag und Leistungszeitraum. */
const suggestionKey = (q: { contractId?: string; servicePeriodStart?: string; servicePeriodEnd?: string }): string =>
  `${q.contractId ?? ""}|${q.servicePeriodStart ?? ""}|${q.servicePeriodEnd ?? ""}`;
const withoutNumber = (q: FormQuery): string => JSON.stringify({ ...q, supplierInvoiceNumber: undefined });

/** Die Prüfung der Maske vor dem Senden (Pflicht, Betragsform) — Schlüssel wie die Feldnamen des Servers. */
function check(d: Draft): Record<string, string> {
  const e: Record<string, string> = {};
  if (d.number.trim() === "") e.supplierInvoiceNumber = T.numberRequired;
  for (const f of ["invoiceDate", "receivedOn", "dueDate"] as const) if (d[f] === "") e[f] = T.dateRequired;
  d.lines.forEach((l, i) => {
    if (amountForApi(l.net, SIGNED) === null) e[`lines[${String(i)}].netAmount`] = T.amountInvalid;
    if (amountForApi(l.tax, SIGNED) === null) e[`lines[${String(i)}].taxAmount`] = T.amountInvalid;
  });
  if (d.kind === "SETTLEMENT" && d.credited.trim() !== "" && amountForApi(d.credited, SIGNED) === null) {
    e.creditedAmount = T.amountInvalid;
  }
  return e;
}

const HEAD_FIELDS = new Set(["supplierInvoiceNumber", "kind", "invoiceDate", "receivedOn", "servicePeriodStart",
  "servicePeriodEnd", "dueDate", "supplierContractId", "paymentMethod", "chargeIds"]);
const LINE_FIELD = /^lines(\[\d+\]\.\w+)?$/;
const knownField = (f: string): boolean => HEAD_FIELDS.has(f) || f === "creditedAmount" || LINE_FIELD.test(f);

// ------------------------------------------------------------------------------------------- Panel

export function ErfassungsPanel(p: {
  customerId: string; companyId: string; client: IncomingPanelClient; vorhandene?: IncomingInvoice;
  /** Die Kontierungslisten des Reiters (`sharedAssignmentLists`, A-K3). */
  lists: Pick<ApiClient, "getBookingAccounts" | "getCostCentres">;
  onGespeichert: (r: IncomingInvoice) => void; onSchliessen: () => void;
  /** Ein Schritt im Browserverlauf (Entscheid 696, PF9): die Zurück-Taste schließt das Panel wie Abbrechen. */
  verlauf?: boolean;
}): ReactElement {
  const { client, customerId, companyId } = p;
  const keys = useRef(0);
  const nextKey: NextKey = () => { keys.current += 1; return keys.current; };
  const [saved, setSaved] = useState<IncomingInvoice | null>(p.vorhandene ?? null);
  const [start, setStart] = useState<Draft>(() => draftOf(p.vorhandene ?? null, nextKey));
  const [draft, setDraft] = useState<Draft>(start);
  const [tried, setTried] = useState(false);
  const [notice, setNotice] = useState<{ text: string; art: "info" | "warnung" }[]>([]);
  const rateState = useActiveRates(client);
  const rates = rateState.rates;
  const [help, setHelp] = useState<{ query: FormQuery; data: IncomingInvoiceFormData } | null>(null);
  const [helpFailed, setHelpFailed] = useState(false);
  const [allCharges, setAllCharges] = useState(false);
  const lists = p.lists;
  /** Die aktiven Konten und Kostenstellen des Unternehmens (dieselben Abfragen wie die Felder, der gemeinsame Speicher
   *  beantwortet sie einmal): nur sie erbt eine Zeile vom Vertrag (Restkorrektur N2). */
  const [active, setActive] = useState<{ accounts: ReadonlySet<string>; costCentres: ReadonlySet<string> } | null>(null);
  useEffect(() => {
    let alive = true;
    Promise.all([lists.getBookingAccounts({ companyId, kind: "EXPENSE" }), lists.getCostCentres({ companyId })]).then(
      ([a, k]) => { if (alive) setActive({ accounts: new Set(a.items.map((x) => x.id)), costCentres: new Set(k.items.map((x) => x.id)) }); },
      () => undefined);
    return () => { alive = false; };
  }, [lists, companyId]);
  const sections = useSectionFolds({ head: true, amounts: true, file: true });
  const savedId = saved?.id ?? null;
  const currency = saved?.currency ?? "EUR";

  /** Vorgabesatz einer neuen Zeile: der höchste aktive (Regelsatz). Ein gesetzter Satz bleibt — auch ein nicht mehr
   *  aktiver einer gespeicherten Zeile (er steht dann als eigene Option da und geht unverändert zurück). */
  const defaultRate = rates.reduce<Steuersatz | null>((m, r) => (m === null || r.ratePercent > m.ratePercent ? r : m), null);
  const rateIdOf = (l: LineDraft): string => (l.taxRateId !== "" ? l.taxRateId : defaultRate?.id ?? "");
  const rateOf = (l: LineDraft): Steuersatz | null => rates.find((r) => r.id === rateIdOf(l)) ?? null;

  // --- Erfassungshilfe: sofort, allein die Rechnungsnummer 250 ms nach der letzten Eingabe -----------------------------
  const query = formQuery(companyId, draft);
  const queryKey = JSON.stringify(query);
  const lastQuery = useRef<FormQuery | null>(null);
  const appliedSuggestion = useRef<string | null>(p.vorhandene === undefined ? null : suggestionKey(query));
  const savedIdRef = useRef(savedId);
  savedIdRef.current = savedId;
  useEffect(() => {
    const previous = lastQuery.current;
    lastQuery.current = query;
    const onlyNumber = previous !== null && withoutNumber(previous) === withoutNumber(query);
    let alive = true;
    const timer = setTimeout(() => {
      client.getIncomingInvoiceFormData(customerId, query).then((data) => {
        if (!alive) return;
        setHelp({ query, data });
        setHelpFailed(false);
        const key = suggestionKey(query);
        if (query.contractId !== undefined && key !== appliedSuggestion.current) {
          appliedSuggestion.current = key;
          const suggested = offeredCharges(data.charges, savedIdRef.current).filter((c) => c.suggested).map((c) => c.id);
          setDraft((d) => ({ ...d, chargeIds: suggested,
            ...(data.creditedDefault === null || d.creditedManual ? {} : { credited: amountField(data.creditedDefault) }) }));
        }
        const due = data.dueDateDefault;
        if (due !== null) setDraft((d) => (d.dueManual || d.dueDate === due ? d : { ...d, dueDate: due }));
      }, () => { if (alive) setHelpFailed(true); });
    }, onlyNumber ? DUPLICATE_DELAY_MS : 0);
    return () => { alive = false; clearTimeout(timer); };
    // Die Abfrage IST der Schlüssel; Klient und Kunde wechseln im offenen Panel nicht.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  const contracts = help?.data.contracts ?? [];
  const chargesLoaded = help !== null && help.query.contractId === (draft.contractId || undefined);
  const offered = chargesLoaded ? offeredCharges(help.data.charges, savedId) : [];
  const duplicate = help?.data.duplicate ?? null;
  const duplicateText = duplicate !== null && !duplicate.cancelled && duplicate.id !== savedId
    && help?.query.supplierInvoiceNumber === draft.number.trim() ? T.duplicate(duplicate.entryNumber) : null;

  // --- Speichern ------------------------------------------------------------------------------------------------------
  function input(): IncomingInvoiceInput {
    const d = draft;
    const settlement = d.kind === "SETTLEMENT";
    const offeredIds = chargesLoaded ? new Set(offered.map((c) => c.id)) : null;
    return {
      supplierInvoiceNumber: d.number.trim(), kind: d.kind, invoiceDate: d.invoiceDate, receivedOn: d.receivedOn,
      servicePeriodStart: d.periodStart === "" ? null : d.periodStart, servicePeriodEnd: d.periodEnd === "" ? null : d.periodEnd,
      dueDate: d.dueDate, supplierContractId: d.contractId === "" ? null : d.contractId, paymentMethod: d.paymentMethod,
      currency, creditedAmount: settlement ? amountForApi(d.credited, SIGNED) : null, note: saved?.note ?? null,
      lines: d.lines.map((l) => ({
        description: l.description, netAmount: amountForApi(l.net, SIGNED)!, taxCase: l.taxCase, taxAmount: amountForApi(l.tax, SIGNED)!,
        taxRateId: l.taxCase === "DOMESTIC" && rateIdOf(l) !== "" ? rateIdOf(l) : null,
        bookingAccountId: l.bookingAccountId, costCentreId: l.costCentreId,
      })),
      chargeIds: !bindsCharges(d.kind) || d.contractId === "" ? []
        : d.chargeIds.filter((id) => offeredIds === null || offeredIds.has(id)),
    };
  }

  const formular = useFormularZustand<IncomingInvoiceSaved>({
    speichern: () => fieldErrorInsteadOf409(() => (saved === null
      ? client.createIncomingInvoice(customerId, { companyId, invoice: input() })
      : client.updateIncomingInvoice(customerId, saved.id, { version: saved.version, invoice: input() })),
    ["EINGANGSRECHNUNG_DOPPELT"]),
    neuLaden: async () => { if (saved !== null) takeOver(await client.getIncomingInvoice(customerId, saved.id)); },
    bekannteFelder: knownField,
  });

  function takeOver(inv: IncomingInvoice): void {
    const d = draftOf(inv, nextKey);
    setSaved(inv);
    setStart(d);
    setDraft(d);
    setTried(false);
  }

  const localErrors = check(draft);
  const errors: Record<string, string> = {
    ...(tried ? localErrors : {}), ...formular.feldFehler,
    ...(duplicateText === null ? {} : { supplierInvoiceNumber: duplicateText }),
  };
  const dirty = JSON.stringify(draft) !== JSON.stringify(start);
  useBeforeUnload(dirty);

  async function save(): Promise<void> {
    setTried(true);
    if (Object.keys(localErrors).length > 0 || duplicateText !== null) return;
    const first = saved === null;
    const r = await formular.aktion();
    if (r === undefined) return;
    p.onGespeichert(r.invoice);
    if (!first && r.warnings.length === 0) { p.onSchliessen(); return; }
    takeOver(r.invoice);
    setNotice([...(first ? [{ text: T.savedHint(r.invoice.entryLabel), art: "info" as const }] : []),
      ...r.warnings.map((w) => ({ text: w.message, art: "warnung" as const }))]);
  }

  async function leave(): Promise<void> {
    if (formular.laeuft) return;
    if (await darfAbbrechen(dirty)) p.onSchliessen();
  }

  // --- Ändern ---------------------------------------------------------------------------------------------------------
  const set = (patch: Partial<Draft>): void => setDraft((d) => ({ ...d, ...patch }));

  /** Eine Zeile ändern; die Steuer folgt Netto × Satz, bis jemand sie selbst setzt, und ist bei anderem Steuerfall 0. */
  function changeLine(i: number, patch: Partial<LineDraft>): void {
    setDraft((d) => ({ ...d, lines: d.lines.map((l, j) => {
      if (j !== i) return l;
      const n = { ...l, ...patch };
      if (n.taxCase !== "DOMESTIC") return { ...n, tax: centsField(0n), taxManual: false };
      if (n.taxManual) return n;
      const net = amountForApi(n.net, SIGNED);
      const rate = rateOf(n);
      // Ein nicht mehr aktiver gespeicherter Satz kennt keinen Prozentsatz mehr: unverändertes Netto → seine Steuer.
      if (rate === null && n.stored !== null && n.stored.taxRateId === rateIdOf(n) && n.stored.net === n.net) {
        return { ...n, tax: n.stored.tax };
      }
      return { ...n, tax: net === null || rate === null ? "" : centsField(taxFor(cents(net), rate.ratePercent)) };
    }) }));
  }

  /** Die Kontierung, die eine Zeile vom Vertrag erbt: nur Konto und Kostenstelle, die im Unternehmen aktiv sind — ein
   *  inzwischen deaktiviertes bliebe sonst als genanntes Feld stehen und brächte 422, wo der Server eine geerbte
   *  Vorgabe verwirft (Ruling W3, B-W3). Ohne geladene Listen bleibt die Zeile ohne Vorgabe. */
  const inherited = (c: { bookingAccountId: string | null; costCentreId: string | null } | undefined): KontierungWert => ({
    bookingAccountId: c?.bookingAccountId != null && active?.accounts.has(c.bookingAccountId) === true ? c.bookingAccountId : null,
    costCentreId: c?.costCentreId != null && active?.costCentres.has(c.costCentreId) === true ? c.costCentreId : null,
  });

  /** Vertrag wählen: Zahlweise, Konto und Kostenstelle aus dem Vertrag; die Posten schlägt die Erfassungshilfe vor. */
  function chooseContract(id: string): void {
    const c = contracts.find((x) => x.id === id);
    // Eine neue Wahl (auch derselbe Vertrag noch einmal) holt den Vorschlag neu.
    appliedSuggestion.current = null;
    setDraft((d) => ({ ...d, contractId: id, chargeIds: [],
      ...(c === undefined ? {} : { paymentMethod: c.paymentMethod, lines: d.lines.map((l) => ({ ...l, ...inherited(c) })) }) }));
  }

  function split(): void {
    if (draft.lines.length >= LINES_MAX) return;
    set({ lines: [...draft.lines, newLine(nextKey, inherited(contracts.find((x) => x.id === draft.contractId)))] });
  }

  // --- Summen ---------------------------------------------------------------------------------------------------------
  const lineCents = (text: string): bigint => { const a = amountForApi(text, SIGNED); return a === null ? 0n : cents(a); };
  const gross = draft.lines.reduce((s, l) => s + lineCents(l.net) + lineCents(l.tax), 0n);
  const payable = draft.kind === "CREDIT_NOTE" ? -gross : draft.kind === "SETTLEMENT" ? gross - lineCents(draft.credited) : gross;

  const count = (pick: (f: string) => boolean): number => Object.keys(errors).filter(pick).length;
  const label = saved?.entryLabel ?? null;
  const showCharges = draft.contractId !== "" && bindsCharges(draft.kind) && offered.length > 0;
  // Sichtbar: vorgeschlagene, gespeicherte und bis zum späteren von Rechnungsdatum und Leistungsende fällige (ohne beides
  // bis heute); der Rest hinter „weitere (n)“ (A-S1). Ein abgewähltes Kästchen bleibt stehen, wo es war.
  const visibleCharges = allCharges ? offered
    : shownCharges(offered, start.chargeIds, [draft.invoiceDate, draft.periodEnd].reduce((a, b) => (b > a ? b : a), "") || today());
  const hiddenCharges = offered.length - visibleCharges.length;
  const date = (f: "invoiceDate" | "receivedOn", text: string): ReactElement => (
    <Eingabe art="datum" beschriftung={text} wert={draft[f]} pflicht {...errorProp(errors[f])}
      onGeaendert={(e) => set({ [f]: e.wert })} />
  );

  const head = (
    <div className="inc__grid">
      <Auswahl beschriftung={T.kindLabel} wert={draft.kind} {...errorProp(errors.kind)}
        onGeaendert={(e) => {
          const kind = e.wert as Kind;
          // Jahresabrechnung und Gutschrift binden keine Posten: die gewählten fallen mit dem Wechsel weg.
          set({ kind, ...(bindsCharges(kind) ? {} : { chargeIds: [] }) });
        }}>
        {KINDS.map((k) => <option key={k} value={k}>{T.kind[k]}</option>)}
      </Auswahl>
      <Auswahl beschriftung={T.contract} wert={draft.contractId} {...errorProp(errors.supplierContractId)}
        onGeaendert={(e) => chooseContract(e.wert)}>
        <option value="">{T.noContract}</option>
        {/* Der gespeicherte Vertrag steht da, bevor die Erfassungshilfe die Liste bringt. */}
        {draft.contractId !== "" && !contracts.some((c) => c.id === draft.contractId) && (
          <option value={draft.contractId}>{saved?.supplierContractTitle ?? T.pending}</option>
        )}
        {contracts.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
      </Auswahl>
      <Eingabe beschriftung={T.number} wert={draft.number} pflicht maxLength={NUMBER_MAX}
        {...errorProp(errors.supplierInvoiceNumber)} onGeaendert={(e) => set({ number: e.wert })} />
      {date("invoiceDate", T.invoiceDate)}
      {date("receivedOn", T.receivedOn)}
      <Eingabe art="datum" beschriftung={T.servicePeriodFrom} wert={draft.periodStart}
        {...errorProp(errors.servicePeriodStart)} onGeaendert={(e) => set({ periodStart: e.wert })} />
      <Eingabe art="datum" beschriftung={T.servicePeriodTo} wert={draft.periodEnd}
        {...errorProp(errors.servicePeriodEnd)} onGeaendert={(e) => set({ periodEnd: e.wert })} />
      <Eingabe art="datum" beschriftung={T.dueDate} wert={draft.dueDate} pflicht {...errorProp(errors.dueDate)}
        onGeaendert={(e) => set({ dueDate: e.wert, dueManual: true })} />
      <Auswahl beschriftung={T.paymentMethod} wert={draft.paymentMethod} {...errorProp(errors.paymentMethod)}
        onGeaendert={(e) => set({ paymentMethod: e.wert as PaymentMethod })}>
        {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{S.paymentMethod[m]}</option>)}
      </Auswahl>
      {showCharges && (
        <div className="inc__charges">
          <Optionen mehrfach beschriftung={T.suggested} {...errorProp(errors.chargeIds)}
            wert={draft.chargeIds.filter((id) => visibleCharges.some((c) => c.id === id))}
            onGeaendert={(e) => set({ chargeIds: [...(e.werte ?? []),
              ...draft.chargeIds.filter((id) => !visibleCharges.some((c) => c.id === id))] })}>
            {visibleCharges.map((c) => <option key={c.id} value={c.id}>{chargeText(c, currency)}</option>)}
          </Optionen>
          {hiddenCharges > 0 && (
            <KiKnopf art="leise" gestalt="pille" onClick={() => setAllCharges(true)}>{T.moreCharges(hiddenCharges)}</KiKnopf>
          )}
        </div>
      )}
    </div>
  );

  const amounts = (
    <div className="inc__amounts">
      {errors.lines !== undefined && <Hinweis art="fehler">{errors.lines}</Hinweis>}
      {draft.lines.map((l, i) => {
        const at = (f: string): string | undefined => errors[`lines[${String(i)}].${f}`];
        const domestic = l.taxCase === "DOMESTIC";
        const rateId = rateIdOf(l);
        const assignmentErrors = {
          ...(at("bookingAccountId") === undefined ? {} : { bookingAccountId: at("bookingAccountId")! }),
          ...(at("costCentreId") === undefined ? {} : { costCentreId: at("costCentreId")! }),
        };
        return (
          <div key={l.key} className="inc__line">
            <div className="inc__line-fields">
              <Eingabe art="dezimal" einheit="€" beschriftung={T.net} wert={l.net} pflicht maxLength={AMOUNT_FIELD_MAX}
                {...errorProp(at("netAmount"))} onGeaendert={(e) => changeLine(i, { net: e.wert })} />
              <Auswahl beschriftung={T.taxCaseLabel} wert={l.taxCase} {...errorProp(at("taxCase"))}
                onGeaendert={(e) => changeLine(i, { taxCase: e.wert as TaxCase, taxManual: false })}>
                {TAX_CASES.map((c) => <option key={c} value={c}>{S.taxCase[c]}</option>)}
              </Auswahl>
              <Auswahl beschriftung={T.taxRate} wert={domestic ? rateId : ""} gesperrt={!domestic}
                {...errorProp(at("taxRateId"))} onGeaendert={(e) => changeLine(i, { taxRateId: e.wert })}>
                {(!domestic || rateId === "") && <option value="">{T.noTaxRate}</option>}
                {domestic && rateId !== "" && !rates.some((r) => r.id === rateId) && (
                  <option value={rateId}>{storedRateText(rateState)}</option>
                )}
                {domestic && rates.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </Auswahl>
              <Eingabe art="dezimal" einheit="€" beschriftung={T.tax} wert={l.tax} gesperrt={!domestic}
                maxLength={AMOUNT_FIELD_MAX} {...errorProp(at("taxAmount"))}
                onGeaendert={(e) => changeLine(i, { tax: e.wert, taxManual: true })} />
            </div>
            <div className="inc__line-assignment">
              <KontierungFelder point="INCOMING_LINE" companyId={companyId} client={lists} hint={false}
                wert={{ bookingAccountId: l.bookingAccountId, costCentreId: l.costCentreId }} fehler={assignmentErrors}
                onWert={(w) => changeLine(i, w)} />
              {draft.lines.length > 1 && (
                <LoeschKnopf name={T.removeLine(i + 1)} onClick={() => set({ lines: draft.lines.filter((_, j) => j !== i) })} />
              )}
            </div>
          </div>
        );
      })}
      {/* Der Hinweis auf die Folgestufe EINMAL unter den Betragszeilen, nicht je Zeile (Nachtrag Review 17). */}
      <AssignmentHint />
      <div className="inc__amounts-foot">
        <KiKnopf deaktiviert={draft.lines.length >= LINES_MAX} onClick={split}>{T.split}</KiKnopf>
        <div className="inc__totals">
          {draft.kind === "SETTLEMENT" && (
            <Eingabe art="dezimal" einheit="€" className="inc__credited" beschriftung={T.credited} wert={draft.credited}
              maxLength={AMOUNT_FIELD_MAX} {...errorProp(errors.creditedAmount)}
              onGeaendert={(e) => set({ credited: e.wert, creditedManual: true })} />
          )}
          <p className="inc__sum"><span>{T.sum}</span><span className="inc__amount">{centsMoney(gross, currency)}</span></p>
          {draft.kind !== "INVOICE" && (
            <p className="inc__payable">
              <span>{T.payable}</span>
              <span className="inc__amount">{centsMoney(payable, currency)}</span>
              {payable < 0n && <Chip statusArt="warnung">{PAYMENT_STATE_TEXT.CREDIT_OPEN}</Chip>}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const messages = [
    ...(formular.meldung === null ? [] : [<Hinweis key="m" art="fehler">{formular.meldung}</Hinweis>]),
    ...(helpFailed ? [<Hinweis key="h" art="warnung">{T.formDataFailed}</Hinweis>] : []),
    ...notice.map((n, i) => <Hinweis key={`n${String(i)}`} art={n.art}>{n.text}</Hinweis>),
  ];

  return (
    <Arbeitsdialog titel={label === null ? T.record : T.editTitle(label)} panel="incoming-invoice"
      {...(p.verlauf === true ? { verlauf: true } : {})}
      onAbbrechen={leave} abbrechenGesperrt={formular.laeuft}
      {...(messages.length === 0 ? {} : { meldungen: <>{messages}</> })}
      fuss={(
        <>
          <KiKnopf art="primaer" deaktiviert={formular.zustand !== "bereit" || duplicateText !== null}
            onClick={() => { void save(); }}>{T.save}</KiKnopf>
          <KiKnopf deaktiviert={formular.laeuft} onClick={() => { void leave(); }}>{T.cancel}</KiKnopf>
        </>
      )}>
      <Formular beschriftung="oben" {...formular.formularProps}>
        <div className="inc__mask">
          <Vorgangsabschnitt art="rechnung" titel={T.sectionHead} faltung={sections.head} inhaltId="inc-kopf"
            fehler={count((f) => HEAD_FIELDS.has(f))}>{head}</Vorgangsabschnitt>
          <Vorgangsabschnitt art="position" titel={T.sectionAmounts} faltung={sections.amounts} inhaltId="inc-betraege"
            zaehler={draft.lines.length} fehler={count((f) => f === "creditedAmount" || LINE_FIELD.test(f))}
            zusammenfassung={centsMoney(gross, currency)}>{amounts}</Vorgangsabschnitt>
          <Vorgangsabschnitt art="datei" titel={T.sectionFile} faltung={sections.file} inhaltId="inc-datei">
            <FileSection client={client} customerId={customerId} invoiceId={savedId} />
          </Vorgangsabschnitt>
        </div>
      </Formular>
    </Arbeitsdialog>
  );
}

/** Abschnitt „Datei“: vor dem ersten Speichern gesperrt (Sofort-Popup), danach Ablage und Hochladen aus 059; scheitert
 *  das Laden der Ablage, sagt es ein Hinweis (der Knopf bleibt gesperrt, aber nicht stumm). */
function FileSection(p: { client: IncomingPanelClient; customerId: string; invoiceId: string | null }): ReactElement {
  const { client, customerId, invoiceId } = p;
  const [state, setState] = useState<{ storage: Ablageort; names: string[] } | null>(null);
  const [failed, setFailed] = useState(false);
  const [upload, setUpload] = useState(false);
  const lock = useSofortPopup<HTMLSpanElement>(invoiceId === null ? T.fileAfterSave : "");
  const bezug = invoiceId === null ? null : { art: "incoming-invoices" as const, id: invoiceId };
  const load = useCallback(async (): Promise<void> => {
    if (invoiceId === null) return;
    try {
      const r = await client.listDocumentFiles(customerId, { art: "incoming-invoices", id: invoiceId });
      setState({ storage: r.storage, names: r.items.map((z) => z.file.originalName) });
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [client, customerId, invoiceId]);
  useEffect(() => { void load(); }, [load]);
  return (
    <div className="inc__file">
      {failed && <Hinweis art="fehler">{DOCUMENT_FILE_TEXT.loadFailed}</Hinweis>}
      {state !== null && (
        <p className="inc__storage">
          {DOCUMENT_FILE_TEXT.storage(state.storage.path)}
          {state.storage.willCreate && <> <span className="inc__muted">{DOCUMENT_FILE_TEXT.willCreate}</span></>}
        </p>
      )}
      {state !== null && state.names.length > 0 && (
        <ul className="inc__file-names">{state.names.map((n) => <li key={n}>{n}</li>)}</ul>
      )}
      <span ref={lock} className="inc__file-lock">
        <KiKnopf deaktiviert={state === null} onClick={() => setUpload(true)}>{DOCUMENT_FILE_TEXT.upload}</KiKnopf>
      </span>
      {upload && bezug !== null && state !== null && (
        <UploadFilesPanel client={client} kundeId={customerId} bezug={bezug} storage={state.storage}
          onChanged={load} onClose={() => setUpload(false)} />
      )}
    </div>
  );
}
